import { useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Encapsulates MediaRecorder + waveform analyser lifecycle.
 * Exposes: { recording, start, stop, canvasRef } where canvasRef must be
 * attached to a <canvas/> element by the caller.
 *
 * onChunk(blob): invoked when recording stops with the final Blob.
 */
export default function useAudioRecorder({ onChunk }) {
  const [recording, setRecording] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const animRef = useRef(null);
  const canvasRef = useRef(null);

  const drawWaveform = (active) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    const analyser = analyserRef.current;

    const render = () => {
      ctx.clearRect(0, 0, w, h);
      if (active && analyser) {
        const buf = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(buf);
        const bars = 40;
        const barW = w / bars;
        for (let i = 0; i < bars; i++) {
          const v = buf[Math.floor((i * buf.length) / bars)] / 255;
          const bh = Math.max(4, v * h * 0.85);
          ctx.fillStyle = `rgba(220, 38, 38, ${0.5 + v * 0.5})`;
          ctx.fillRect(i * barW + 2, (h - bh) / 2, barW - 4, bh);
        }
      } else {
        ctx.fillStyle = "rgba(220, 38, 38, 0.3)";
        for (let i = 0; i < 40; i++) {
          ctx.fillRect(i * (w / 40) + 2, h / 2 - 2, (w / 40) - 4, 4);
        }
      }
      animRef.current = requestAnimationFrame(render);
    };
    render();
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (onChunk) await onChunk(blob);
      };
      mr.start();
      mediaRecRef.current = mr;
      setRecording(true);

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      source.connect(analyser);
      analyserRef.current = analyser;
      drawWaveform(true);
    } catch (e) {
      toast.error("No se pudo acceder al micrófono. Revisa permisos.");
      if (process.env.NODE_ENV !== "production") {
        console.error(e);
      }
    }
  };

  const stop = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      mediaRecRef.current.stop();
    }
    setRecording(false);
    if (animRef.current) cancelAnimationFrame(animRef.current);
    drawWaveform(false);
  };

  const cleanup = () => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    if (audioCtxRef.current) audioCtxRef.current.close();
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      mediaRecRef.current.stop();
    }
  };

  return { recording, start, stop, cleanup, canvasRef, drawIdle: () => drawWaveform(false) };
}
